import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Input, Button, FormCard } from "./FormComponents";

function friendlyError(code?: string): string {
  switch (code) {
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/user-not-found":
      return "If an account exists with this email, a reset link has been sent.";
    case "auth/too-many-requests":
      return "Too many attempts. Please try again later.";
    default:
      return "An unexpected error occurred. Please try again.";
  }
}

export default function PasswordReset() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      // Call the new SendGrid-powered password reset function
      const functions = getFunctions();
      const sendPasswordReset = httpsCallable(functions, 'sendPasswordResetViaSendGrid');

      await sendPasswordReset({ email: email.trim() });

      setSuccess("Password reset email sent! Please check your inbox and follow the instructions.");
      setEmail(""); // Clear the email field on success
    } catch (err) {
      const errorCode = (err as { code?: string })?.code;
      setError(friendlyError(errorCode));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormCard title="Reset Password">
      {/* Description */}
      <p className="text-center text-base text-gray-700">
        Enter your email address and we'll send you a link to reset your password.
      </p>

      {/* Success Message */}
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg" role="alert">
          <p>{success}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg" role="alert">
          <p>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '2rem', marginTop: '1rem' }}>
        <Input
          id="email"
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          disabled={isLoading}
          variant="underline"
        />

        <Button type="submit" disabled={isLoading} variant="brown">
          {isLoading ? "Sending..." : "Reset Password"}
        </Button>
      </form>

      {/* Back to Sign In Link */}
      <p className="text-center text-base text-gray-700" style={{ marginTop: '0.5rem' }}>
        Remember your password?{" "}
        <Link to="/login" className="font-normal text-primary-600 hover:text-primary-700">
          Log In
        </Link>
      </p>
    </FormCard>
  );
}
